import {
  ARTWORK_BACKDROP_MOTION,
  getBackdropTransitionMotion,
  type BackdropTransitionMotion
} from "../design/backdropMotion";
import {
  artworkFocalY,
  PAGE_SCRIM_MID_STOP,
  pageScrimStops
} from "../design/backdropTone";

const BACKDROP_OPACITY_VALUE = 0.45;
const BACKDROP_OPACITY = String(BACKDROP_OPACITY_VALUE);
const HOST_ID = "recital-master-persistent-backdrop";

function backdropTransition(motion: BackdropTransitionMotion): string {
  return `opacity ${motion.durationMs}ms cubic-bezier(${motion.easing.join(", ")})`;
}

type BackdropElements = {
  host: HTMLDivElement;
  layers: [HTMLDivElement, HTMLDivElement];
};

export type WebBackdropRevealMode = "crossfade" | "fade-in" | "instant";
export type WebPersistentBackdropSnapshot = {
  composed: boolean;
  uri: string | null;
};

/** @internal Exported so the blank-route history invariant stays unit-tested. */
export function getWebBackdropRevealMode(
  hasComposed: boolean,
  outgoingUri: string | null
): WebBackdropRevealMode {
  if (!hasComposed) return "instant";
  return !outgoingUri ? "fade-in" : "crossfade";
}

/**
 * Freeze an interrupted artwork-to-artwork blend without dimming the canvas.
 * Browser history gestures can reverse while the previous 1 s crossfade is
 * still running, so both computed layer opacities are meaningful. Preserve
 * their ratio and normalize their combined coverage instead of snapping the
 * newly requested layer to zero (which exposes the black body for a frame).
 */
export function stabilizeWebArtworkOpacities(
  opacities: readonly [number, number],
  incomingLayer: number
): [number, number] {
  const clamped = opacities.map((opacity) =>
    Number.isFinite(opacity)
      ? Math.min(BACKDROP_OPACITY_VALUE, Math.max(0, opacity))
      : 0
  ) as [number, number];
  const total = clamped[0] + clamped[1];
  if (total <= 0.0001) {
    const outgoingLayer = incomingLayer === 0 ? 1 : 0;
    clamped[outgoingLayer] = BACKDROP_OPACITY_VALUE;
    return clamped;
  }
  const scale = BACKDROP_OPACITY_VALUE / total;
  return [clamped[0] * scale, clamped[1] * scale];
}

let elements: BackdropElements | null = null;
let currentUri: string | null = null;
// Null is a legitimate composed state for routes such as Account. Keep that
// separate from a compositor that has never received its first route request;
// otherwise returning from a blank route is mistaken for cold start and the
// artwork is revealed instantly instead of fading normally.
let hasComposedRoute = false;
// The focal anchor of the artwork currently composed, so a route that requests
// the same image with a different anchor still repositions.
let currentFocalY: number | null = null;
let requestVersion = 0;
let transitionFrame: number | null = null;
let transitionVersion = 0;

/** The compositor state that survives Expo Router's React-tree remounts. */
export function getWebPersistentBackdropSnapshot(): WebPersistentBackdropSnapshot {
  return {
    composed: hasComposedRoute,
    uri: currentUri
  };
}

function imageLayer(): HTMLDivElement {
  const layer = document.createElement("div");
  Object.assign(layer.style, {
    // Set per request from the focused route's focal value; see setLayerUri.
    backgroundPosition: `center ${artworkFocalY()}%`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    filter: "blur(60px)",
    height: "115%",
    left: "-6%",
    opacity: "0",
    position: "absolute",
    top: "-8%",
    transform: "translateZ(0)",
    transition: backdropTransition(ARTWORK_BACKDROP_MOTION),
    width: "112%",
    willChange: "opacity"
  });
  return layer;
}

function ensureElements(): BackdropElements | null {
  if (typeof document === "undefined") return null;
  if (elements?.host.isConnected) return elements;

  const existing = document.getElementById(HOST_ID);
  if (existing) existing.remove();

  const host = document.createElement("div");
  host.id = HOST_ID;
  Object.assign(host.style, {
    height: "460px",
    left: "0",
    overflow: "hidden",
    pointerEvents: "none",
    position: "fixed",
    right: "0",
    top: "0",
    zIndex: "0"
  });

  const first = imageLayer();
  const second = imageLayer();
  const gradient = document.createElement("div");
  Object.assign(gradient.style, {
    background: (() => {
      const [top, mid, bottom] = pageScrimStops();
      return `linear-gradient(${top}, ${mid} ${PAGE_SCRIM_MID_STOP}%, ${bottom} 100%)`;
    })(),
    inset: "0",
    position: "absolute"
  });

  host.append(first, second, gradient);

  document.body.prepend(host);
  elements = {
    host,
    layers: [first, second]
  };
  return elements;
}

function setLayerUri(
  backdrop: BackdropElements,
  index: number,
  uri: string,
  focalY?: number | null
): void {
  const image = `url(${JSON.stringify(uri)})`;
  const layer = backdrop.layers[index];
  layer.dataset.uri = uri;
  layer.style.backgroundImage = image;
  // The focal anchor belongs to the route being composed, not to the
  // compositor, so it is applied alongside the incoming bitmap. Both layers
  // therefore keep the anchor matching the artwork they are showing, and a
  // crossfade between a cover page and a stills page never mismatches them.
  setLayerFocal(backdrop, index, focalY);
}

function setLayerFocal(
  backdrop: BackdropElements,
  index: number,
  focalY?: number | null
): void {
  backdrop.layers[index].style.backgroundPosition =
    `center ${artworkFocalY(focalY)}%`;
}

function setLayerOpacity(
  backdrop: BackdropElements,
  index: number,
  opacity: string
): void {
  backdrop.layers[index].style.opacity = opacity;
}

function setLayerTransition(
  backdrop: BackdropElements,
  index: number,
  transition: string
): void {
  backdrop.layers[index].style.transition = transition;
}

function readLayerOpacity(layer: HTMLDivElement): number {
  const value =
    typeof window !== "undefined" && typeof window.getComputedStyle === "function"
      ? window.getComputedStyle(layer).opacity
      : layer.style.opacity;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function readLayerOpacities(backdrop: BackdropElements): [number, number] {
  return [
    readLayerOpacity(backdrop.layers[0]),
    readLayerOpacity(backdrop.layers[1])
  ];
}

function setLayerOpacities(
  backdrop: BackdropElements,
  opacities: readonly [number, number]
): void {
  setLayerOpacity(backdrop, 0, String(opacities[0]));
  setLayerOpacity(backdrop, 1, String(opacities[1]));
}

function cancelTransitionFrame(): void {
  transitionVersion += 1;
  if (transitionFrame != null) cancelAnimationFrame(transitionFrame);
  transitionFrame = null;
}

function scheduleTransitionFrame(run: () => void): void {
  if (transitionFrame != null) cancelAnimationFrame(transitionFrame);
  const version = ++transitionVersion;
  transitionFrame = requestAnimationFrame(() => {
    transitionFrame = null;
    if (version === transitionVersion) run();
  });
}

function settleImage(uri: string): Promise<boolean> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      if (typeof image.decode !== "function") {
        resolve(true);
        return;
      }
      image.decode().then(() => resolve(true), () => resolve(true));
    };
    image.onerror = () => resolve(false);
    image.src = uri;
  });
}

/**
 * Update the web backdrop that lives outside React's root. Expo Router may
 * remount the entire React layout during browser history traversal; these DOM
 * layers remain physically mounted and therefore cannot flash between roots.
 */
export async function requestWebPersistentBackdrop(
  uri: string | null,
  focalY: number | null,
  onSettled: (uri: string | null, loaded: boolean) => void
): Promise<void> {
  const backdrop = ensureElements();
  if (!backdrop) {
    onSettled(uri, false);
    return;
  }

  const version = ++requestVersion;
  if (hasComposedRoute && uri === currentUri) {
    // Same artwork, different anchor: reposition both layers in place. There is
    // no new bitmap, so there is nothing to blend and no readiness to await.
    if (focalY !== currentFocalY) {
      setLayerFocal(backdrop, 0, focalY);
      setLayerFocal(backdrop, 1, focalY);
      currentFocalY = focalY;
    }
    onSettled(uri, true);
    return;
  }

  if (!uri) {
    cancelTransitionFrame();
    const transition = backdropTransition(
      getBackdropTransitionMotion(currentUri, null)
    );
    // A history traversal can reach a blank route during an artwork
    // crossfade, when both layers are visible. Fade both; clearing only the
    // logical target can leave the interrupted outgoing artwork behind.
    setLayerTransition(backdrop, 0, transition);
    setLayerTransition(backdrop, 1, transition);
    setLayerOpacity(backdrop, 0, "0");
    setLayerOpacity(backdrop, 1, "0");
    currentUri = null;
    currentFocalY = null;
    hasComposedRoute = true;
    onSettled(null, true);
    return;
  }

  const loaded = await settleImage(uri);
  if (version !== requestVersion) return;
  if (!loaded) {
    onSettled(uri, false);
    return;
  }

  cancelTransitionFrame();
  const existingLayer = backdrop.layers.findIndex((layer) => layer.dataset.uri === uri);
  const liveOpacities = readLayerOpacities(backdrop);
  // A third artwork can arrive while the prior two are blending. Reuse the
  // less-visible layer so replacing its bitmap cannot disturb the established
  // visual baseline.
  const incomingLayer =
    existingLayer >= 0
      ? existingLayer
      : liveOpacities[0] <= liveOpacities[1]
        ? 0
        : 1;
  const outgoingLayer = 1 - incomingLayer;
  const incoming = backdrop.layers[incomingLayer];
  const transition = backdropTransition(getBackdropTransitionMotion(currentUri, uri));
  const revealMode = getWebBackdropRevealMode(hasComposedRoute, currentUri);

  // Only the compositor's first route request is an instant reveal beneath
  // the launch curtain. React readiness can reset during browser-history
  // remounts, and null can mean an intentionally blank route; neither is a
  // cold start for this module-level compositor.
  if (revealMode === "instant") {
    setLayerUri(backdrop, incomingLayer, uri, focalY);
    setLayerTransition(backdrop, incomingLayer, "none");
    setLayerOpacity(backdrop, incomingLayer, BACKDROP_OPACITY);
    if (incomingLayer !== outgoingLayer) {
      setLayerOpacity(backdrop, outgoingLayer, "0");
    }
    // Force the instant state to commit before restoring future transitions.
    void incoming.offsetWidth;
    setLayerTransition(
      backdrop,
      incomingLayer,
      backdropTransition(ARTWORK_BACKDROP_MOTION)
    );
  } else if (revealMode === "fade-in") {
    setLayerUri(backdrop, incomingLayer, uri, focalY);
    // Returning from a blank route may reverse its short fade while it is in
    // flight. Retarget both live layers; never reset the requested one to zero.
    setLayerTransition(backdrop, outgoingLayer, transition);
    setLayerTransition(backdrop, incomingLayer, transition);
    setLayerOpacity(backdrop, outgoingLayer, "0");
    setLayerOpacity(backdrop, incomingLayer, BACKDROP_OPACITY);
  } else {
    const crossfadeStart =
      existingLayer >= 0
        ? stabilizeWebArtworkOpacities(liveOpacities, incomingLayer)
        : incomingLayer === 0
          ? [0, BACKDROP_OPACITY_VALUE] as [number, number]
          : [BACKDROP_OPACITY_VALUE, 0] as [number, number];
    setLayerTransition(backdrop, 0, "none");
    setLayerTransition(backdrop, 1, "none");
    setLayerOpacities(backdrop, crossfadeStart);
    // A new URI is assigned only after its reusable layer is hidden. Cached
    // history destinations keep their live opacity, so reversing a gesture
    // continues the blend from the exact compositor frame already on screen.
    setLayerUri(backdrop, incomingLayer, uri, focalY);
    void backdrop.host.offsetWidth;
    setLayerTransition(backdrop, outgoingLayer, transition);
    setLayerTransition(backdrop, incomingLayer, transition);
    scheduleTransitionFrame(() => {
      setLayerOpacity(backdrop, outgoingLayer, "0");
      setLayerOpacity(backdrop, incomingLayer, BACKDROP_OPACITY);
    });
  }

  currentUri = uri;
  currentFocalY = focalY;
  hasComposedRoute = true;
  onSettled(uri, true);
}
